
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useTeams } from '@/hooks/useTeams';
import { playerConnectionsService, PlayerTeamConnection } from '@/services/playerConnectionsService';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Users, MapPin, Trophy, UserPlus, Filter, ArrowRight, ExternalLink } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { Team } from '@/types/database';
import { TeamManagementPermissions } from '@/components/teams/TeamManagementPermissions';

const TeamsSearch = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const { teams, loading } = useTeams();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [requestedTeams, setRequestedTeams] = useState<Set<number>>(new Set());
  const [existingConnections, setExistingConnections] = useState<PlayerTeamConnection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  
  // Filter states
  const [sportFilter, setSportFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const isMobile = useIsMobile();

  // Check if user is authenticated
  const isAuthenticated = !!(supabaseUser || internalUser);
  const userProfile = profile || internalProfile;
  const currentUserId = (supabaseUser?.id || internalUser?.id) as string;

  // Ensure page scrolls to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Load existing connections
  useEffect(() => {
    const loadConnections = async () => {
      if (!currentUserId) return;
      
      try {
        setLoadingConnections(true);
        const connections = await playerConnectionsService.getPlayerTeamConnections(currentUserId);
        setExistingConnections(connections);
      } catch (error) {
        console.error('Error loading team connections:', error);
      } finally {
        setLoadingConnections(false);
      }
    };

    loadConnections();
  }, [currentUserId]);

  // Filter teams based on search query and filters
  useEffect(() => {
    let filtered = teams;

    // Apply text search
    if (searchQuery.trim()) {
      filtered = filtered.filter(team =>
        team.team_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        team.sport_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        team.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        team.city?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sport filter (case-insensitive)
    if (sportFilter !== 'all') {
      filtered = filtered.filter(team => 
        team.sport_type?.toLowerCase() === sportFilter.toLowerCase()
      );
    }

    // Apply location filter
    if (locationFilter.trim()) {
      filtered = filtered.filter(team =>
        team.location?.toLowerCase().includes(locationFilter.toLowerCase()) ||
        team.city?.toLowerCase().includes(locationFilter.toLowerCase()) ||
        team.state_province?.toLowerCase().includes(locationFilter.toLowerCase()) ||
        team.continent?.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }

    // Apply age group filter
    if (ageGroupFilter !== 'all') {
      filtered = filtered.filter(team => team.age_group === ageGroupFilter);
    }

    setFilteredTeams(filtered);
  }, [teams, searchQuery, sportFilter, locationFilter, ageGroupFilter]);

  const getConnectionStatus = (teamId: number) => {
    // Check if current user is the team creator
    const team = teams.find(t => t.team_id === teamId);
    if (team && team.created_by === currentUserId) {
      return 'connected';
    }
    
    const connection = existingConnections.find(conn => conn.team_id === teamId);
    if (!connection) return 'none';
    
    if (connection.status === 'current' && connection.confirmed) return 'connected';
    if (connection.status === 'current' && !connection.confirmed) return 'pending';
    if (connection.status === 'applied_to') return 'requested';
    
    return 'none';
  };

  const canRequestConnection = (teamId: number) => {
    const status = getConnectionStatus(teamId);
    return status === 'none' && !requestedTeams.has(teamId);
  };

  const getButtonText = (teamId: number) => {
    const status = getConnectionStatus(teamId);
    
    if (requestedTeams.has(teamId)) return 'Request Sent';
    if (status === 'connected') return 'Connected';
    if (status === 'pending') return 'Request Pending';
    if (status === 'requested') return 'Request Sent';
    
    return 'Request to Connect';
  };

  const handleConnectionRequest = async (teamId: number) => {
    if (!currentUserId || !canRequestConnection(teamId)) return;

    try {
      // Determine user type based on which auth system they're using
      const userType = supabaseUser ? 'supabase' : 'internal';
      
      // Create the connection request
      await playerConnectionsService.createTeamConnection(
        currentUserId,
        teamId,
        'applied_to'
      );

      // Update local state
      setRequestedTeams(prev => new Set([...prev, teamId]));
      
      // Add to existing connections for immediate UI update
      const newConnection: PlayerTeamConnection = {
        id: crypto.randomUUID(),
        player_id: currentUserId,
        team_id: teamId,
        status: 'applied_to',
        confirmed: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setExistingConnections(prev => [...prev, newConnection]);

      toast.success('Connection request sent! Team owner and coaches will review your request.');
    } catch (error) {
      console.error('Error sending connection request:', error);
      toast.error('Failed to send connection request. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-2">
              <Search className="h-8 w-8 text-sports-green" />
              Find a Team
            </h1>
            <p className="text-gray-400">
              Discover and join teams in your area
            </p>
          </div>

          {/* Search and Filter Section */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Search & Filter Teams
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by team name, sport, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-gray-900 border-gray-700 text-white"
                />
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Select value={sportFilter} onValueChange={setSportFilter}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Sports</SelectItem>
                    <SelectItem value="soccer">Soccer</SelectItem>
                    <SelectItem value="basketball">Basketball</SelectItem>
                    <SelectItem value="baseball">Baseball</SelectItem>
                    <SelectItem value="football">Football</SelectItem>
                    <SelectItem value="hockey">Hockey</SelectItem>
                    <SelectItem value="tennis">Tennis</SelectItem>
                    <SelectItem value="golf">Golf</SelectItem>
                    <SelectItem value="volleyball">Volleyball</SelectItem>
                    <SelectItem value="track-field">Track & Field</SelectItem>
                    <SelectItem value="swimming">Swimming</SelectItem>
                    <SelectItem value="wrestling">Wrestling</SelectItem>
                    <SelectItem value="gymnastics">Gymnastics</SelectItem>
                    <SelectItem value="cross-country">Cross Country</SelectItem>
                    <SelectItem value="softball">Softball</SelectItem>
                    <SelectItem value="lacrosse">Lacrosse</SelectItem>
                  </SelectContent>
                </Select>

                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Location (city, state, country)"
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-10 bg-gray-900 border-gray-700 text-white"
                  />
                </div>

                <Select value={ageGroupFilter} onValueChange={setAgeGroupFilter}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Age Group" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Ages</SelectItem>
                    <SelectItem value="youth">Youth (Under 18)</SelectItem>
                    <SelectItem value="u6">Under 6</SelectItem>
                    <SelectItem value="u8">Under 8</SelectItem>
                    <SelectItem value="u10">Under 10</SelectItem>
                    <SelectItem value="u12">Under 12</SelectItem>
                    <SelectItem value="u14">Under 14</SelectItem>
                    <SelectItem value="u16">Under 16</SelectItem>
                    <SelectItem value="u18">Under 18</SelectItem>
                    <SelectItem value="18-21">18-21</SelectItem>
                    <SelectItem value="21-plus">21+</SelectItem>
                    <SelectItem value="25-plus">25+</SelectItem>
                    <SelectItem value="30-plus">30+</SelectItem>
                    <SelectItem value="adult">Adult</SelectItem>
                    <SelectItem value="senior">Senior (50+)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={divisionFilter} onValueChange={setDivisionFilter}>
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Division" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all">All Divisions</SelectItem>
                    <SelectItem value="recreational">Recreational</SelectItem>
                    <SelectItem value="competitive">Competitive</SelectItem>
                    <SelectItem value="elite">Elite</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="division 1">Division 1</SelectItem>
                    <SelectItem value="division 2">Division 2</SelectItem>
                    <SelectItem value="division 3">Division 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Teams Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sports-green"></div>
              <span className="ml-3 text-gray-400">Loading teams...</span>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">
                {searchQuery ? 'No Teams Found' : 'No Teams Available'}
              </h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? 'Try adjusting your search terms or browse all teams.'
                  : 'There are no teams available to join at the moment.'
                }
              </p>
              {searchQuery && (
                <Button
                  onClick={() => setSearchQuery('')}
                  variant="outline"
                  className="mt-4 border-gray-700 text-white hover:bg-gray-800"
                >
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTeams.map((team) => (
                <Card 
                  key={team.team_id} 
                  className="bg-gray-900 border-gray-700 overflow-hidden hover:bg-gray-800 transition-all duration-300 hover:shadow-lg hover:shadow-sports-green/20"
                >
                  <div className="relative">
                    {/* Team background image with dark overlay for readability */}
                    <div 
                      className="h-56 relative"
                      style={{
                        backgroundImage: team.team_logo 
                          ? `url(${team.team_logo})`
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(59, 130, 246, 0.9) 50%, rgba(139, 69, 19, 0.9) 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      {/* 80% dark overlay for text visibility */}
                      <div className="absolute inset-0 bg-black/80" />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/60" />
                      
                      {/* Sport badge */}
                      <Badge className="absolute top-4 left-4 bg-sports-green text-black font-bold px-3 py-1 text-sm">
                        {team.sport_type || 'General'}
                      </Badge>
                      
                      {/* Member count */}
                      <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-full border border-white/20">
                        <Users className="h-4 w-4 text-white" />
                        <span className="text-white font-semibold">{Math.floor(Math.random() * 25) + 5} Members</span>
                      </div>
                      
                      {/* Team name */}
                      <div className="absolute bottom-16 left-4 right-20">
                        <h3 className="text-3xl font-bold text-white drop-shadow-lg" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.8)' }}>
                          {team.team_name}
                        </h3>
                      </div>
                      
                      {/* Team circle avatar/logo */}
                      <div className="absolute bottom-4 right-4">
                        <div className="w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden shadow-lg">
                          {team.team_logo ? (
                            <img 
                              src={team.team_logo} 
                              alt={`${team.team_name} logo`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-sports-green to-blue-600 flex items-center justify-center">
                              <span className="text-white font-bold text-xl">
                                {team.team_name.charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="p-6 bg-gray-900">
                    {/* Location */}
                    <div className="flex items-center gap-2 text-gray-300 mb-4">
                      <MapPin className="h-5 w-5 text-sports-green" />
                      <span className="font-medium">
                        {team.city && team.state_province 
                          ? `${team.city}, ${team.state_province}`
                          : team.location || 'Location TBD'
                        }
                      </span>
                    </div>
                    
                    {/* Connection Button */}
                    <Button 
                      className="w-full bg-sports-green text-black hover:bg-green-400 font-bold py-3 text-lg transition-all duration-200"
                      onClick={() => handleConnectionRequest(team.team_id)}
                      disabled={!canRequestConnection(team.team_id)}
                    >
                      {getButtonText(team.team_id)}
                      <ExternalLink className="h-5 w-5 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default TeamsSearch;
